import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEarlyWarningsFilters } from '../../../hooks/useEarlyWarningsFilters';
import { FilterConfigValues, IconCode, TabFilterOption } from '../../../models/EarlyWarnings';
import { getProjectsSimpleTableIndicator } from '../../../redux/actions/indicatorProjects';
import { PROJECTS_PARTIAL_CANCELLATIONS } from '../../../services/sessionFiltersIndicatorDetailHelper';
import { EarlyWarningsFilters as WithPartialCancellationsEarlyWarningsFilters } from '../EarlyWarningsFilters/EarlyWarningsFilters';
import styles from './ProjectsWithPartialCancellations.module.scss';
import { WithPartialCancellationsTable } from './Table/Table';
import { createFilterConfigComponent } from '../EarlyWarningsFilters/Util';
import { showEarlyWarning } from '../PortfolioDetailTablesUtil';
import { handleLoading } from '../../utils';
import FilterIndicators from '../../../models/FilterIndicators';
import { AppMenuTabs } from '../../MenuTabs/userTabsInterface';
import { setIndicatorResponse, setLoading } from '../../../redux/actions/dataTable';
import { getNewFilteredIndicators } from '../../../api/indicatorApi';
import { translateMultipleText } from '../../../services/stringUtil';
import { emptyProjects } from '../../Portfolio/PortfolioUtil';
import { useTranslation } from 'react-i18next';
import { ProjectProductTypes } from '../../../models/ProjectProductTypes';
import { processIndicators } from '../../Portfolio/indicatorHelper';
import { arrayMove } from 'react-sortable-hoc';
import { addIndicators } from '../../../redux/actions/indicators';
import { resetIndicatorDetailFilter } from '../../../redux/actions/indicatorDetailFilters';
import { updateCreatingOrEditing } from '../../../redux/actions/menuTabs';
import Filter from '../../Portfolio/Filter/Filter';
import { setCurrentIndicator } from '../../../redux/actions/currentIndicator';
import { AppState } from '../../../redux/store';
import ErrorMessageView from '../../Util/ErrorMessage/ErrorMessageView';
import { error } from 'console';


const filterConfigValues: FilterConfigValues[] = [
  { id: 0, filterIcon: IconCode.GREEN, filterLabel: 'no_partial_cancellations' },
  { id: 1, filterIcon: IconCode.YELLOW, filterLabel: 'with_cancellations_of_up_to_15' },
  { id: 2, filterIcon: IconCode.RED, filterLabel: 'with_cancellations_higher_than_15' },
];
const filterConfigComponent: TabFilterOption[] = createFilterConfigComponent(filterConfigValues);

const defaultSessionFilterObject = {
  filterConfigState: filterConfigComponent,
  changeProjectType: true,
};

export const ProjectsWithPartialCancellations = () => {
  const dispatch = useDispatch();
  const { container, earlyAlertsFilterContainer, bodyContainer, ErrorMessageStyle } = styles;
  const { currentIndicator } = useSelector<any, any>(state => state);
  const { projectTypeFilterOnDetail } = useSelector<any, any>(state => state.indicatorDetailFilter);
  const { filterConfigState, handleEarlyWarningsFilter, handleProjectsQuantities } = useEarlyWarningsFilters({
    sessionKey: PROJECTS_PARTIAL_CANCELLATIONS,
    defaultSessionFilterObject,
    projectTypeFilterOnDetail,
  });
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [hasError, setHasError] = useState(false)
  const { t } = useTranslation();

  // Filter
  const filterIndicators = useSelector<any, FilterIndicators>(state => state.filterIndicators);
  const stateProjectProductTypes = useSelector<any, FilterIndicators>(
    state => state.indicatorPayload.payload.projectProductTypes,
  );
  const menuTabs = useSelector<any, AppMenuTabs>(state => state.menuTabs);
  const portfolioSelectedTab = menuTabs.portfolioSelectedTab;
  const creatingOrEditing = menuTabs.creatingOrEditing;
  const [isLoading, setIsLoading] = React.useState(false);
  const [totalProjects, setTotalProjects] = React.useState(false);
  const [flag, setFlag] = useState<boolean>(false);

  const projectType = useSelector(
    (state: AppState) => state.indicatorDetailFilter.projectTypeFilterOnDetail
  );


  const normalizedProjectType =
    projectType === 0
      ? [0, 1]
      : projectType != null
        ? [projectType]
        : filterIndicators.common.projectTypeFilters;



  const projectTypeFilterChange = {
    ...filterIndicators,
    common: {
      ...filterIndicators.common,
      projectTypeFilters: normalizedProjectType,
    },
    projectTypeFilters: normalizedProjectType,
  };


  const handleCommonFilterChange = async () => {
    dispatch(setLoading(true));
    setIsLoading(true);
    setHasError(false);

    try {
      const indicatorsToProcess = await getNewFilteredIndicators(
        {},
        { projectProductTypes: stateProjectProductTypes },
        {
          ...projectTypeFilterChange.common,
        },
      );
      dispatch(setIndicatorResponse(indicatorsToProcess));
      startProcessIndicators(
        indicatorsToProcess,
        translateMultipleText(` ${' '} ${portfolioSelectedTab.title}`, t),
        filterIndicators.common.projectTypeFilters,
      );
      setTotalProjects(emptyProjects(indicatorsToProcess.totalProjects));
    } catch (err) {
      console.error(err, 'Error fetching data from indicators');
      setHasError(true);
    } finally {
      dispatch(setLoading(false));
      setIsLoading(false);
    }
  };

  const startProcessIndicators = (inds, titleFromTab, projectTypeFilter = [ProjectProductTypes.Loan]) => {
    const result = processIndicators(inds, {
      titleFromTab,
      projectTypeFilter,
    });
    const elementSummary: any = result.find(elem => elem.id === 15);
    const indexSummary: number = result.indexOf(elementSummary);
    const sendEndSummary = arrayMove(result, indexSummary, result.length);

    dispatch(addIndicators(sendEndSummary));
    dispatch(setCurrentIndicator(sendEndSummary?.find(data => data.indicatorKey === 'PROJECTS_PARTIAL_CANCELLATIONS')));
    setIsLoading(false);
  };

  // const tabId0OrCreatingEditing = (menuTabs, creatingOrEditing) =>
  //   menuTabs.portfolioSelectedTab.tabId === 0 || creatingOrEditing;

  useEffect(() => {
    if (projectType === null) return;
    getFilteredSimpleIndicators(
      dispatch,
      currentIndicator.id,
      currentIndicator.indicatorKey,
      setLoadingData,
      projectTypeFilterChange,
      setHasError,
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndicator.id, dispatch, projectTypeFilterOnDetail, filterIndicators.common]);

  useEffect(() => {
    if (!sessionStorage.getItem(PROJECTS_PARTIAL_CANCELLATIONS)) {
      sessionStorage.setItem(PROJECTS_PARTIAL_CANCELLATIONS, JSON.stringify(defaultSessionFilterObject));
    }
  }, []);

  useEffect(() => {
    if (
      filterIndicators.common.coReponsibleDivisions.length > 0 ||
      filterIndicators.common.countryId.length > 0 ||
      filterIndicators.common.divisionId.length > 0 ||
      filterIndicators.common.interestIssues.length > 0 ||
      filterIndicators.common.participantDivisions.length > 0 ||
      filterIndicators.common.regionId.length > 0 ||
      filterIndicators.common.sectors.length > 0 ||
      filterIndicators.common.teamLeaders.length > 0
    )
      handleCommonFilterChange();
    setFlag(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  useEffect(() => {
    //dispatch(resetIndicatorDetailFilter());
    if (
      !menuTabs.firstLoad &&
      // tabId0OrCreatingEditing(menuTabs, creatingOrEditing) &&
      filterIndicators.common.coReponsibleDivisions.length === 0 &&
      filterIndicators.common.countryId.length === 0 &&
      filterIndicators.common.divisionId.length === 0 &&
      filterIndicators.common.interestIssues.length === 0 &&
      filterIndicators.common.participantDivisions.length === 0 &&
      filterIndicators.common.regionId.length === 0 &&
      filterIndicators.common.sectors.length === 0 &&
      filterIndicators.common.teamLeaders.length === 0
    ) {
      handleCommonFilterChange();
      setFlag(true);

      if (creatingOrEditing) {
        dispatch(updateCreatingOrEditing(false));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  return (
    <div className={container}>
      {!hasError ? (
        <>
          {showEarlyWarning(projectTypeFilterOnDetail) && (
            <div className={earlyAlertsFilterContainer}>
              <WithPartialCancellationsEarlyWarningsFilters
                filterSet={filterConfigState}
                handleFilter={handleEarlyWarningsFilter}
              />
            </div>
          )}
          <div className={bodyContainer}>
            <WithPartialCancellationsTable
              projectsQuantities={handleProjectsQuantities}
              earlyWarningsTab={[
                filterConfigState[0].selected,
                filterConfigState[1].selected,
                filterConfigState[2].selected,
              ]}
              loading={loadingData}
            />
          </div>
        </>
      ) : (
        <div className={ErrorMessageStyle}>
          <ErrorMessageView
            title={t('title_error_message_view')}
            description={t('desc_error_message_tab')}
            reload={handleCommonFilterChange}
            isIndicator={true}
          />
        </div>
      )}

    </div>
  );
};

const getFilteredSimpleIndicators = (
  d,
  currentIndicatorId,
  currentIndicatorKey,
  setLoading,
  customFilterIndicators?,
  setHasError?,
) => {
  try {
    setHasError?.(false);
    return handleLoading(
      () => d(getProjectsSimpleTableIndicator(currentIndicatorId, currentIndicatorKey, customFilterIndicators)),
      setLoading,
    );
  } catch (err) {
    console.error(err, 'Error fetching filtered simple indicators');
    setHasError?.(true);
  }
};
